import { useCallback, useMemo, useReducer } from "react"
import axios, { CancelToken } from "axios"
import { ApiAccess } from 'features/session/types'
import { useApiAccess, useWebSockets } from "features/session/context"

type PostListState<TSummary> = {
  posting: boolean
  count: number
  current: number
  summary: TSummary | null
  error: Error | null
}
type PostListAction<TSummary> =
  | { type: 'clear' }
  | { type: 'start'; count: number }
  | { type: 'partial'; current: number }
  | { type: 'error'; error: Error }
  | { type: 'finish'; summary: TSummary | null }

const postListReducer = <TSummary>(
  state: PostListState<TSummary>,
  action: PostListAction<TSummary>
): PostListState<TSummary> => {
  switch (action.type) {
    case 'clear':
      return { ...state, posting: false, error: null, summary: null }
    case 'start':
      return { ...state, posting: true, error: null, count: action.count, summary: null }
    case 'partial':
      return { ...state, error: null, current: action.current }
    case 'error':
      return { ...state, error: action.error, summary: null }
    case 'finish':
      return { ...state, posting: false, summary: action.summary }
  }
}

export const useApiPostList = <TParams, TResult, TSummary = TResult>(
  postFunc: (
    apiAccess: ApiAccess,
    params: TParams,
    from: number,
    to: number,
    cancelToken: CancelToken,
    webSocketId?: string
  ) => Promise<TResult> | null,
  resolve: (
    results: TResult[],
    params: TParams,
    apiAccess: ApiAccess,
    cancelToken: CancelToken
  ) => TSummary,
  reject: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reason: any | undefined,
    params: TParams
  ) => void,
  deps?: React.DependencyList
): [PostListState<TSummary>, (params: TParams, count: number, limit: number) => void] => {
  const socket = useWebSockets()
  const cancel = axios.CancelToken.source()
  const [state, dispatch] = useReducer(
    postListReducer, {
      posting: false,
      count: 0,
      current: 0,
      error: null,
      summary: null
    } as PostListState<TSummary>
  )
  const apiAccess = useApiAccess()
  const post = useCallback(
    async (params: TParams, count: number, limit: number): Promise<void> => {
      dispatch({ type: 'start', count })

      let hasError = false
      const results: TResult[] = []
      for (let from = 0; from < count; from += limit) {
        const to = Math.min(count, from + limit)
        try {
          const promise = postFunc(apiAccess, params, from, to, cancel.token, socket?.id)
          if (!promise) {
            dispatch({ type: 'clear' })
            return
          }
          const result = await promise
          results.push(result)
          dispatch({ type: 'partial', current: to })
        } catch(error) {
          dispatch({ type: 'error', error: error as Error })
          if (reject) reject(error, params)
          hasError = true
        }
        if (hasError) break
      }
      if (!hasError) {
        const summary = resolve(results, params, apiAccess, cancel.token)
        dispatch({ type: 'finish', summary })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    (deps ?? []).concat([/*apiAccess, cancel, */postFunc, resolve, dispatch])
  )
  return useMemo(
    () => [state as PostListState<TSummary>, post],
    [state, post]
  )
}
